import React from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion } from 'framer-motion';
import { GitMerge, RotateCcw, Target, BarChart3, Zap } from 'lucide-react';
export function AllTogetherView({
  state,
  resetApp
}: StepProps) {
  const specialties = [{
    id: 'Nephrologist',
    color: SPECIALTY_COLORS.Nephrologist
  }, {
    id: 'Cardiologist',
    color: SPECIALTY_COLORS.Cardiologist
  }, {
    id: 'Endocrinologist',
    color: SPECIALTY_COLORS.Endocrinologist
  }, {
    id: 'PCP',
    color: SPECIALTY_COLORS.PCP
  }];
  const rows = [{
    label: 'Proactivity',
    Nephrologist: 'Late',
    Cardiologist: 'Early',
    Endocrinologist: 'Mid',
    PCP: 'Late'
  }, {
    label: 'Primary Driver',
    Nephrologist: 'Safety Data',
    Cardiologist: 'Outcomes',
    Endocrinologist: 'Guidelines',
    PCP: 'Peer Behavior'
  }, {
    label: 'Key Barrier',
    Nephrologist: 'Late Referrals',
    Cardiologist: 'Workload',
    Endocrinologist: 'Guidance',
    PCP: 'Guidance'
  }, {
    label: 'Treatment Approach',
    Nephrologist: 'Cautious / Reactive',
    Cardiologist: 'Aggressive / Proactive',
    Endocrinologist: 'Metabolic Focus',
    PCP: 'Conservative'
  }];
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto bg-gray-50 pt-20" data-id="element-44">
      <div className="max-w-6xl w-full mx-auto" data-id="element-45">
        <div className="text-center mb-12" data-id="element-46">
          <div className="inline-flex items-center justify-center p-4 bg-dark text-white rounded-full mb-6" data-id="element-47">
            <GitMerge size={32} data-id="element-48" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-dark mb-4" data-id="element-49">
            The Full Picture
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-50">
            Each group explored a piece of the puzzle. Together, we see the
            system.
          </p>
        </div>

        {/* Comparison Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden mb-12" data-id="element-51">
          <div className="overflow-x-auto" data-id="element-52">
            <table className="w-full text-left border-collapse" data-id="element-53">
              <thead data-id="element-54">
                <tr data-id="element-55">
                  <th className="p-6 bg-gray-50 border-b border-gray-200 w-1/5" data-id="element-56"></th>
                  {specialties.map(spec => {
                  const isSelected = state.selectedSpecialty === spec.id;
                  return <th key={spec.id} className={`p-6 border-b border-gray-200 w-1/5 text-center ${isSelected ? 'bg-gray-50' : ''}`} data-id="element-57">
                        <span className="text-lg font-bold block" style={{
                      color: spec.color
                    }} data-id="element-58">
                          {spec.id}
                        </span>
                        {isSelected && <span className="text-xs uppercase tracking-wider text-gray-500 mt-1 block" data-id="element-59">
                            Your Group
                          </span>}
                      </th>;
                })}
                </tr>
              </thead>
              <tbody data-id="element-60">
                {rows.map((row, i) => <tr key={row.label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-id="element-61">
                    <td className="p-6 border-b border-gray-100 font-bold text-gray-700 text-sm uppercase tracking-wider" data-id="element-62">
                      {row.label}
                    </td>
                    {specialties.map(spec => {
                  const isSelected = state.selectedSpecialty === spec.id;
                  return <td key={`${row.label}-${spec.id}`} className={`p-6 border-b border-gray-100 text-center font-medium ${isSelected ? 'text-dark font-bold bg-gray-50/50' : 'text-gray-800'}`} data-id="element-63">
                          {row[spec.id as keyof typeof row]}
                        </td>;
                })}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* System Gap */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-dark text-white rounded-3xl p-10 text-center shadow-xl max-w-4xl mx-auto mb-12" data-id="element-64">
          <h3 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" data-id="element-65">
            Differences between specialties contribute to delayed optimal
            treatment in up to <span className="text-brand" data-id="element-66">~50% of cases</span>
            .
          </h3>
          <div className="w-24 h-1 bg-gray-700 mx-auto mb-6" data-id="element-67"></div>
          <p className="text-xl text-gray-300 leading-relaxed" data-id="element-68">
            The same message won't work for every specialty. Engagement must be
            tailored to their specific drivers and barriers.
          </p>
        </motion.div>

        {/* Workshop Objectives Recap */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="mb-12" data-id="element-69">
          <h3 className="text-center text-sm font-bold uppercase tracking-widest text-gray-500 mb-6" data-id="element-70">
            Apply your insights to these three objectives
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-71">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-72">
              <div className="flex items-center mb-3" data-id="element-73">
                <div className="w-8 h-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center mr-3" data-id="element-74">
                  <Target size={18} data-id="element-75" />
                </div>
                <span className="text-xs font-bold text-brand uppercase tracking-wider" data-id="element-76">
                  Objective A
                </span>
              </div>
              <h4 className="text-lg font-bold text-dark mb-2" data-id="element-77">
                Segmentation & Targeting
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-78">
                Each specialty requires different identification criteria and
                prioritisation. Use your group's insights to sharpen who you
                target and when.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-79">
              <div className="flex items-center mb-3" data-id="element-80">
                <div className="w-8 h-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center mr-3" data-id="element-81">
                  <BarChart3 size={18} data-id="element-82" />
                </div>
                <span className="text-xs font-bold text-brand uppercase tracking-wider" data-id="element-83">
                  Objective B
                </span>
              </div>
              <h4 className="text-lg font-bold text-dark mb-2" data-id="element-84">
                Features & Arguments
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-85">
                Different specialties respond to different evidence and product
                features. Prioritise what resonates, not what's easiest to say.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-86">
              <div className="flex items-center mb-3" data-id="element-87">
                <div className="w-8 h-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center mr-3" data-id="element-88">
                  <Zap size={18} data-id="element-89" />
                </div>
                <span className="text-xs font-bold text-brand uppercase tracking-wider" data-id="element-90">
                  Objective C
                </span>
              </div>
              <h4 className="text-lg font-bold text-dark mb-2" data-id="element-91">
                The Right Motivation
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-92">
                Understanding what drives each specialty to act — and what holds
                them back — is the key to effective engagement.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Closing */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="text-center max-w-3xl mx-auto mb-8" data-id="element-93">
          <p className="text-2xl font-bold text-dark leading-relaxed" data-id="element-94">
            Each group now has a unique perspective. Together, you see the full
            system.
          </p>
        </motion.div>

        <div className="flex justify-center pb-12" data-id="element-95">
          <button onClick={resetApp} className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors" data-id="element-96">
            <RotateCcw className="mr-2 w-4 h-4" data-id="element-97" />
            Restart Workshop
          </button>
        </div>
      </div>
    </div>;
}